import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Divider,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Folder as FolderIcon,
  Memory as MemoryIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import axios from 'axios';

const DATA_TYPES = ['Boolean', 'Int16', 'Int32', 'Int64', 'Float', 'Double', 'String', 'DateTime'];
const DIRECTIONS = ['read', 'write', 'readwrite'];
const CATEGORIES = ['status', 'production', 'order', 'config', 'custom'];

const OPCUANodesManager = ({ machineId, machineName, onClose }) => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [browseDialogOpen, setBrowseDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeToDelete, setNodeToDelete] = useState(null);

  // Browse state
  const [browseNodes, setBrowseNodes] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browsePath, setBrowsePath] = useState([{ nodeId: 'i=85', name: 'Objects' }]);

  // Node values
  const [nodeValues, setNodeValues] = useState({});
  const [loadingValues, setLoadingValues] = useState(false);

  // Current tab
  const [currentTab, setCurrentTab] = useState(0);

  // Form state for edit dialog
  const [formData, setFormData] = useState({
    node_id: '',
    name: '',
    description: '',
    data_type: 'String',
    direction: 'read',
    category: 'custom',
    unit: '',
    is_active: true,
    poll_interval_ms: 1000
  });

  const loadNodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/machines/${machineId}/opcua-nodes`);
      setNodes(response.data);
    } catch (err) {
      console.error('Error loading nodes:', err);
      setError('Errore nel caricamento dei nodi');
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  const loadNodeValues = useCallback(async () => {
    if (nodes.length === 0) return;

    try {
      setLoadingValues(true);
      const response = await axios.post(`/api/machines/${machineId}/opcua-nodes/read-values`);
      setNodeValues(response.data.values || {});
    } catch (err) {
      console.error('Error loading node values:', err);
    } finally {
      setLoadingValues(false);
    }
  }, [machineId, nodes.length]);

  useEffect(() => {
    loadNodes();
  }, [loadNodes]);

  useEffect(() => {
    if (nodes.length > 0 && currentTab === 1) {
      loadNodeValues();
    }
  }, [nodes, currentTab, loadNodeValues]);

  const handleAdd = () => {
    setSelectedNode(null);
    setFormData({
      node_id: '',
      name: '',
      description: '',
      data_type: 'String',
      direction: 'read',
      category: 'custom',
      unit: '',
      is_active: true,
      poll_interval_ms: 1000
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (node) => {
    setSelectedNode(node);
    setFormData({
      node_id: node.node_id,
      name: node.name,
      description: node.description || '',
      data_type: node.data_type || 'String',
      direction: node.direction || 'read',
      category: node.category || 'custom',
      unit: node.unit || '',
      is_active: node.is_active,
      poll_interval_ms: node.poll_interval_ms || 1000
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (node) => {
    setNodeToDelete(node);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/machines/${machineId}/opcua-nodes/${nodeToDelete.id}`);
      setDeleteConfirmOpen(false);
      setNodeToDelete(null);
      setSuccess('Nodo eliminato con successo');
      loadNodes();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nell\'eliminazione del nodo');
    }
  };

  const handleSave = async () => {
    try {
      if (selectedNode) {
        await axios.put(`/api/machines/${machineId}/opcua-nodes/${selectedNode.id}`, formData);
        setSuccess('Nodo aggiornato con successo');
      } else {
        await axios.post(`/api/machines/${machineId}/opcua-nodes`, formData);
        setSuccess('Nodo creato con successo');
      }
      setEditDialogOpen(false);
      loadNodes();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nel salvataggio del nodo');
    }
  };

  const handleToggleActive = async (node) => {
    try {
      await axios.post(`/api/machines/${machineId}/opcua-nodes/toggle-active`, {
        node_ids: [node.id],
        is_active: !node.is_active
      });
      loadNodes();
    } catch (err) {
      setError('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleImportDefaults = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/machines/${machineId}/opcua-nodes/import-defaults`);
      setSuccess(response.data.message);
      loadNodes();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nell\'importazione dei nodi default');
    } finally {
      setLoading(false);
    }
  };

  // Browse functionality
  const handleOpenBrowse = () => {
    setBrowsePath([{ nodeId: 'i=85', name: 'Objects' }]);
    setBrowseNodes([]);
    setBrowseDialogOpen(true);
    browseTo('i=85');
  };

  const browseTo = async (nodeId) => {
    try {
      setBrowseLoading(true);
      const response = await axios.post(`/api/machines/${machineId}/opcua-nodes/browse`, { nodeId });
      setBrowseNodes(response.data.nodes || []);
    } catch (err) {
      setError('Errore nel browse dei nodi: ' + (err.response?.data?.error || err.message));
    } finally {
      setBrowseLoading(false);
    }
  };

  const handleBrowseNavigate = (node) => {
    if (node.nodeClass === 'Object' || node.nodeClass === 'View') {
      setBrowsePath([...browsePath, { nodeId: node.nodeId, name: node.displayName }]);
      browseTo(node.nodeId);
    }
  };

  const handleBrowseBack = () => {
    if (browsePath.length > 1) {
      const newPath = browsePath.slice(0, -1);
      setBrowsePath(newPath);
      browseTo(newPath[newPath.length - 1].nodeId);
    }
  };

  const handleBrowseAddNode = async (node) => {
    try {
      await axios.post(`/api/machines/${machineId}/opcua-nodes/add-from-browse`, {
        nodeId: node.nodeId,
        displayName: node.displayName,
        dataType: node.dataType,
        readable: node.readable,
        writable: node.writable,
        category: 'custom'
      });
      setSuccess(`Nodo "${node.displayName}" aggiunto`);
      loadNodes();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Nodo gia\' presente per questa macchina');
      } else {
        setError(err.response?.data?.error || 'Errore nell\'aggiunta del nodo');
      }
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      status: 'primary',
      production: 'success',
      order: 'warning',
      config: 'info',
      custom: 'default'
    };
    return colors[category] || 'default';
  };

  const getDirectionLabel = (direction) => {
    const labels = {
      read: 'Lettura',
      write: 'Scrittura',
      readwrite: 'Lettura/Scrittura'
    };
    return labels[direction] || direction;
  };

  const formatValue = (value) => {
    if (value === undefined || value === null) return '-';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return value.toFixed(2);
    return String(value);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onClose && (
            <IconButton onClick={onClose}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h5">
            Nodi OPC UA - {machineName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleImportDefaults}
            disabled={loading}
          >
            Importa Default
          </Button>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={handleOpenBrowse}
          >
            Esplora Server
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Aggiungi Nodo
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
          <Tab label="Configurazione" />
          <Tab label="Valori Live" />
        </Tabs>
      </Box>

      {currentTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Attivo</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Node ID</TableCell>
                <TableCell>Tipo Dato</TableCell>
                <TableCell>Direzione</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Unita'</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : nodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Nessun nodo configurato. Clicca "Importa Default" o "Esplora Server" per iniziare.
                  </TableCell>
                </TableRow>
              ) : (
                nodes.map((node) => (
                  <TableRow key={node.id}>
                    <TableCell>
                      <Switch
                        checked={node.is_active}
                        onChange={() => handleToggleActive(node)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {node.name}
                      </Typography>
                      {node.description && (
                        <Typography variant="caption" color="text.secondary">
                          {node.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                        {node.node_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{node.data_type}</TableCell>
                    <TableCell>{getDirectionLabel(node.direction)}</TableCell>
                    <TableCell>
                      <Chip
                        label={node.category}
                        size="small"
                        color={getCategoryColor(node.category)}
                      />
                    </TableCell>
                    <TableCell>{node.unit || '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifica">
                        <IconButton size="small" onClick={() => handleEdit(node)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina">
                        <IconButton size="small" onClick={() => handleDelete(node)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {currentTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              startIcon={loadingValues ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={loadNodeValues}
              disabled={loadingValues}
            >
              Aggiorna Valori
            </Button>
          </Box>
          <Grid container spacing={2}>
            {nodes.filter(n => n.is_active).map((node) => (
              <Grid item xs={12} sm={6} md={4} key={node.id}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      {node.name}
                    </Typography>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {formatValue(nodeValues[node.node_id]?.value)}
                      {node.unit && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          {node.unit}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {node.node_id}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedNode ? 'Modifica Nodo' : 'Nuovo Nodo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Node ID"
              value={formData.node_id}
              onChange={(e) => setFormData({ ...formData, node_id: e.target.value })}
              placeholder="ns=2;s=Machine.Status"
              required
              fullWidth
            />
            <TextField
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Descrizione"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Dato</InputLabel>
                  <Select
                    value={formData.data_type}
                    label="Tipo Dato"
                    onChange={(e) => setFormData({ ...formData, data_type: e.target.value })}
                  >
                    {DATA_TYPES.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Direzione</InputLabel>
                  <Select
                    value={formData.direction}
                    label="Direzione"
                    onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                  >
                    {DIRECTIONS.map(dir => (
                      <MenuItem key={dir} value={dir}>{getDirectionLabel(dir)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={formData.category}
                    label="Categoria"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {CATEGORIES.map(cat => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Unita' di misura"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="pz/min, C, %"
                  fullWidth
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Attivo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>

      {/* Browse Dialog */}
      <Dialog open={browseDialogOpen} onClose={() => setBrowseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Esplora Server OPC UA
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Breadcrumbs>
              {browsePath.map((item, index) => (
                <Link
                  key={index}
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setBrowsePath(browsePath.slice(0, index + 1));
                    browseTo(item.nodeId);
                  }}
                  underline={index === browsePath.length - 1 ? 'none' : 'hover'}
                  color={index === browsePath.length - 1 ? 'text.primary' : 'inherit'}
                >
                  {item.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          {browsePath.length > 1 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBrowseBack}
              sx={{ mb: 2 }}
            >
              Indietro
            </Button>
          )}

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Tipo Dato</TableCell>
                  <TableCell>Valore</TableCell>
                  <TableCell>Accesso</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {browseLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : browseNodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nessun nodo trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  browseNodes.map((node, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{ cursor: node.nodeClass === 'Object' ? 'pointer' : 'default' }}
                      onClick={() => node.nodeClass === 'Object' && handleBrowseNavigate(node)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {node.nodeClass === 'Object' ? (
                            <FolderIcon fontSize="small" color="primary" />
                          ) : (
                            <MemoryIcon fontSize="small" color="action" />
                          )}
                          {node.displayName}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={node.nodeClass} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{node.dataType || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {formatValue(node.currentValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {node.nodeClass === 'Variable' && (
                          <>
                            {node.readable && <Chip label="R" size="small" sx={{ mr: 0.5 }} />}
                            {node.writable && <Chip label="W" size="small" color="warning" />}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {node.nodeClass === 'Variable' && (
                          <Tooltip title="Aggiungi nodo">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBrowseAddNode(node);
                              }}
                              color="primary"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrowseDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare il nodo "{nodeToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annulla</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OPCUANodesManager;
